import { useState, useEffect } from "react";
import Box from "@mui/system/Box";
import Typography from "@mui/material/Typography";
import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';

// Local imports
import { containerStyles } from "../Dashboard/Dashboard";
import { api } from '../../api';
import Notification from "../Notification";

const boxStyles = {
    ...containerStyles,
    backgroundColor: '#f5f5f5',
    borderRadius: 2,
    padding: 2,
}

const respBoxStyles= {
    bgcolor: '#e0dfdfff', 
    p: 2, 
    borderRadius: 2
}

const SurveyResponsesTabPanel = ({ value, index, responses, questionData }) => {
    const [modifiedResponses, setModifiedResponses] = useState([]);
    const [audioError, setAudioError] = useState('');

    useEffect(() => {
        if (responses.length === 0) return;

        const responsesWithAudioUrls = async () => {
            const updatedResponses = await Promise.all(responses.map(async (resp) => {
                if (resp.audios3Key) {
                    const audioUrl = await fetchAudioUrl(resp.username, resp.questionId);
                    return { ...resp, audioUrl };
                }
                return resp;
            }));
            setModifiedResponses(updatedResponses);
        };

        responsesWithAudioUrls();
    }, [responses]);

    const fetchAudioUrl = async(username, questionId) => {
        try {
            const response = await api.get(`/survey/audio/response?username=${username}&questionId=${questionId}`);
            return response.audioUrl;
        } catch (err) {
            console.error('Error fetching presigned audio URL:', err);
            setAudioError(`Error fetching audio file. ${err}`);
            return null;
        }
    }

    return (
         <div
            role="tabpanel"
            hidden={value !== index}
            id={`topic-tabpanel-${index}`}
            aria-labelledby={`topic-tab-${index}`}
        >
            {value === index && (
                <>
                {/* Topic name and question */}
                <Box sx={boxStyles}>
                    <Typography variant="h6" sx={{ fontWeight: '700'}}>{questionData ? questionData.topicName : 'Topic not found'}</Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1 }}>{questionData ? questionData.description : ''}</Typography>
                </Box>

                {responses.length === 0 ? (
                    <Typography variant="body1" sx={{ mt: 2 }}>No responses found for this topic.</Typography>
                ) : (
                    modifiedResponses.map((resp, idx) => {
                        return (
                            <Box key={idx} sx={boxStyles}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #ccc', pb: 1, mb: 1 }}>
                                    <Box sx={{ display: 'flex' }}>
                                        <Avatar sx={{ bgcolor: 'primary.light', mr: 1 }}>
                                            <PersonIcon />
                                        </Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>{resp.username}</Typography>
                                            <Typography variant="body2" sx={{ color: 'grey' }}>{new Date(resp.createdAt).toLocaleString()}</Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex' }}>
                                        {resp.textResponse && (
                                            <ChatBubbleOutlineOutlinedIcon sx={{ color: 'grey', mr: 1 }} />
                                        )}
                                        {resp.audios3Key && (
                                            <MicNoneOutlinedIcon sx={{ color: 'grey', mr: 1 }} />
                                        )}
                                    </Box>
                                </Box>
                                <Box>
                                    {resp.textResponse && (
                                        <Box sx={{ mb: 2, ...(resp.audios3Key && {borderBottom: '1px solid #e0dfdf82', pb: 1 }) }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'grey' }}>
                                                <ChatBubbleOutlineOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                                Text Response
                                            </Typography>
                                            <Typography variant="body1" sx={{ ...respBoxStyles, mb: 1 }}>{resp.textResponse}</Typography>
                                        </Box>
                                    )}
                                    {resp.audios3Key && (
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'grey' }}>
                                                <MicNoneOutlinedIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                                                Audio Response
                                            </Typography>

                                            {/* Audio error notification */}
                                            {audioError && <Notification message={audioError} severity="error" />}
                                            
                                            {resp.audioUrl && (
                                                <Box sx={{ 
                                                        ...respBoxStyles,
                                                        display: 'inline-block', 
                                                        width: '100%'
                                                    }}>
                                                    <audio 
                                                        controls 
                                                        src={resp.audioUrl}
                                                        style={{ width: '100%' }}
                                                    />
                                                </Box>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )
                    }) 
                )}
                </>
            )}
        </div>
    )
};

export default SurveyResponsesTabPanel;